import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

export interface Office {
  city: string
  country: string
  address: string
  isHq?: boolean
}

interface OfficeGridProps {
  offices: Office[]
}

export default function OfficeGrid({ offices }: OfficeGridProps) {
  return (
    <Section tone="subtle" density="spacious">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>Offices</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            Four cities,
            <br />
            one platform.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            From New York to Istanbul, our team operates close to the customers
            we serve. Reach the office nearest you, or any of them.
          </Text>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {offices.map((office, i) => (
            <motion.div
              key={office.city}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
              className="rounded-3xl border border-line-subtle bg-surface p-8 transition duration-300 ease-smooth hover:border-line hover:shadow-card-hover"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-50 text-brand-900">
                <PinIcon />
              </div>
              <div className="mt-6 flex items-center gap-2">
                <h3 className="text-display-sm font-medium text-ink-primary">
                  {office.city}
                </h3>
                {office.isHq && (
                  <span className="inline-flex items-center rounded-pill bg-accent-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent-700">
                    HQ
                  </span>
                )}
              </div>
              <Text size="sm" tone="tertiary" className="mt-1">
                {office.country}
              </Text>
              <address className="mt-4 not-italic text-body-sm text-ink-secondary">
                {office.address}
              </address>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

function PinIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M10 18s6-5.2 6-10A6 6 0 0 0 4 8c0 4.8 6 10 6 10z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}
